/**
 * Text Popup interaction layer.
 *
 * Adds click-to-open behaviour on top of the base anchored-popover-component
 * (which provides hover open/close). Click-opened popups persist through
 * hover-leave and close on:
 *   - clicking the trigger again
 *   - clicking outside (Popover API light dismiss)
 *   - the pointer leaving the parent marquee-component (mouse only)
 *
 * Positioned via a delegated `beforetoggle` listener that clamps the popup
 * within viewport margins.
 */

const POPOVER_SELECTOR = '.text-popup__popover';
const TRIGGER_SELECTOR = '.text-popup__trigger';

const clickState = new WeakMap();

/** @returns {{ clickOpened: boolean, allowClose: boolean, wasClickOpen: boolean }} */
function getState(popover) {
  if (!clickState.has(popover)) {
    clickState.set(popover, { clickOpened: false, allowClose: false, wasClickOpen: false });
  }
  return clickState.get(popover);
}

// --- Positioning ---

document.addEventListener(
  'beforetoggle',
  (e) => {
    const popover = e.target;
    if (!popover.matches(POPOVER_SELECTOR) || e.newState !== 'open') return;
    const component = popover.closest('anchored-popover-component');
    if (!component) return;
    const trigger = component.querySelector('[ref="trigger"]');
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const margin = 16;
    const inherited = parseInt(getComputedStyle(popover).getPropertyValue('--popover-max-width'));
    const preferredMax = inherited || 280;
    const vw = document.documentElement.clientWidth;
    const maxW = Math.min(preferredMax, vw - margin * 2);
    const idealLeft = rect.left + rect.width / 2 - maxW / 2;
    const clampedLeft = Math.max(margin, Math.min(idealLeft, vw - margin - maxW));

    popover.style.setProperty('--anchor-top', `${rect.top}`);
    popover.style.setProperty('--popover-left', `${clampedLeft}`);
    popover.style.setProperty('--popover-max-width', `${maxW}`);
  },
  true
);

// --- Click-to-open / persist / marquee-leave ---

/**
 * Runs before light dismiss (which fires on pointerdown).
 * Marks whether the close should be allowed so the beforetoggle
 * handler below can decide whether to block the component's
 * hover-leave hidePopover() calls.
 */
document.addEventListener(
  'pointerdown',
  (e) => {
    const openPopovers = document.querySelectorAll(`${POPOVER_SELECTOR}:popover-open`);
    if (openPopovers.length === 0) return;

    openPopovers.forEach((popover) => {
      const s = clickState.get(popover);
      if (!s?.clickOpened) return;
      const component = popover.closest('anchored-popover-component');
      if (!component) return;
      const trigger = component.querySelector(TRIGGER_SELECTOR);

      if (trigger?.contains(e.target)) {
        s.wasClickOpen = true;
        s.allowClose = true;
      } else if (!popover.contains(e.target)) {
        s.allowClose = true;
      }
    });
  },
  true
);

/**
 * Blocks the component's hover-leave hidePopover() when the popup
 * was opened by click. Allows close when explicitly permitted
 * (outside click / trigger re-click / marquee leave).
 */
document.addEventListener(
  'beforetoggle',
  (e) => {
    if (e.newState !== 'closed') return;
    const popover = e.target;
    if (!popover.matches(POPOVER_SELECTOR)) return;
    const s = clickState.get(popover);
    if (!s?.clickOpened) return;

    if (s.allowClose) {
      s.clickOpened = false;
      s.allowClose = false;
      return;
    }
    e.preventDefault();
  },
  true
);

/**
 * Click trigger to open. If it was already click-opened,
 * light dismiss closed it on pointerdown — skip re-opening.
 */
document.addEventListener('click', (e) => {
  const trigger = e.target.closest(TRIGGER_SELECTOR);
  if (!trigger) return;
  const component = trigger.closest('anchored-popover-component');
  if (!component) return;
  const popover = component.querySelector(`${POPOVER_SELECTOR}[popover]`);
  if (!popover) return;
  const s = getState(popover);

  if (s.wasClickOpen) {
    s.wasClickOpen = false;
    return;
  }

  if (!popover.matches(':popover-open')) {
    s.clickOpened = true;
    s.allowClose = false;
    popover.showPopover();
  }
});

/**
 * Close when pointer leaves the marquee (animation resumes).
 * Touch is excluded — the marquee never truly stops on touch.
 */
document.addEventListener(
  'pointerleave',
  (e) => {
    if (e.pointerType === 'touch') return;
    if (!e.target.matches?.('marquee-component')) return;
    e.target.querySelectorAll(`${POPOVER_SELECTOR}:popover-open`).forEach((popover) => {
      const s = clickState.get(popover);
      if (s) s.allowClose = true;
      popover.hidePopover();
    });
  },
  true
);
